import React, { useState, useEffect } from 'react';
import { XMarkIcon, BellIcon } from '@heroicons/react/24/outline';

const STORAGE_KEY = 'hcai_reminder_dismissed';
// Show after 45 seconds of browsing if the user hasn't signed up or dismissed
const DELAY_MS = 45_000;

export default function ReminderModal() {
  const [visible, setVisible] = useState(false);
  const [contact, setContact] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed) return;

    const timer = setTimeout(() => setVisible(true), DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setVisible(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const trimmed = contact.trim();
    if (!trimmed) {
      setError('Please enter an email address or phone number.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/reminders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact: trimmed }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Something went wrong. Please try again.');
        return;
      }
      setSubmitted(true);
      localStorage.setItem(STORAGE_KEY, 'true');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative animate-fade-up">
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
          aria-label="Close"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>

        {submitted ? (
          <div className="text-center py-4">
            <div className="w-12 h-12 bg-sky-50 rounded-full flex items-center justify-center mx-auto mb-3">
              <BellIcon className="w-6 h-6 text-sky-500" />
            </div>
            <h3 className="font-bold text-slate-800 text-lg mb-1">You're on the list!</h3>
            <p className="text-slate-500 text-sm">
              We'll send you a weekly reminder. You can unsubscribe any time.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-sky-50 rounded-xl flex items-center justify-center shrink-0">
                <BellIcon className="w-5 h-5 text-sky-500" />
              </div>
              <div>
                <h3 className="font-bold text-slate-800 text-base leading-tight">
                  Want a reminder to come back?
                </h3>
                <p className="text-slate-500 text-xs mt-0.5">
                  We'll send one reminder per week — no spam, unsubscribe any time.
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Email address or phone number
              </label>
              <input
                type="text"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder="you@email.com or +1 555 000 0000"
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent"
                autoComplete="email"
              />
              {error && (
                <p className="text-red-500 text-xs mt-1.5">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="mt-3 w-full bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white font-semibold text-sm py-2.5 rounded-lg transition-colors"
              >
                {loading ? 'Saving…' : 'Send me weekly reminders'}
              </button>
            </form>

            <p className="text-xs text-slate-400 text-center mt-3">
              No account needed. We never share your contact details.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
